import { FC, useContext, useEffect, useMemo, useState } from 'react';

import { Placeholder, useContent } from '@eneco/cms';
import { maskHouseNumber } from '@eneco/common/maskInput/maskHouseNumber';
import PostalCodeInput from '@eneco/components/PostalCodeInput/PostalCodeInput';
import RichText from '@eneco/components/RichText/RichText';
import { TrackedDialog } from '@eneco/components/TrackedDialog/TrackedDialog';
import { useAddressFormWatcher, useAddressPublicApi } from '@eneco/hooks/address';
import { typedYupResolver } from '@eneco/hooks/form';
import { useFormatter } from '@eneco/i18n';
import { Button, Form, Heading, InputSelect, InputText, NavLink, Stack, Text, TextLink } from '@eneco/sparky';
import { ChevronLeftIcon, InfoIcon } from '@eneco/sparky/icons';
import { TrackViewComponent } from '@eneco/tracking';
import { Controller, SubmitHandler, useForm, useWatch } from 'react-hook-form';

import { Action } from '../registrationFlow.constants';
import { RegistrationMachine } from '../registrationFlow.machine';
import { RegistrationAccountContent } from '../registrationFlow.types';
import { canRegisterAddressSchema } from './canRegisterAddressSchema';

interface Props {
  trackingStart: () => void;
}

type FormValues = {
  houseNumber: string;
  houseNumberSuffix: string;
  postalCode: string;
};

export const CanRegisterAddress: FC<Props> = ({ trackingStart }) => {
  const { send, state } = useContext(RegistrationMachine);
  const content = useContent<RegistrationAccountContent>();
  const { address } = useFormatter();
  const [formHasReceivedFocus, setFormHasReceivedFocus] = useState(false);
  const [isSuffixRequired, setIsSuffixRequired] = useState(false);
  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    resetField,
  } = useForm<FormValues>({
    defaultValues: {
      postalCode: state.context.postalCode,
      houseNumber: String(state.context.houseNumber || ''),
      houseNumberSuffix: state.context.houseNumberSuffix || '',
    },
    resolver: typedYupResolver<FormValues>(canRegisterAddressSchema(isSuffixRequired)),
  });
  const { houseNumberSuffix: houseNumberSuffixValue, postalCode, houseNumber } = useWatch({ control });

  const { parameters, houseNumberSuffix, isValidInput, emptySuffixString } = useAddressFormWatcher({
    houseNumberSuffix: houseNumberSuffixValue,
    postalCode,
    houseNumber,
  });

  const { data, error, isValidating, houseNumberSuffixOptions } = useAddressPublicApi(parameters);

  const isCurrentAddressValidated = useMemo(() => {
    if (!data && !error) return false;
    if (!isValidInput || !postalCode || !houseNumber) return false;

    // there can be a null option in the suffix options, so null can be valid even if suffix is required
    const isHouseSuffixMissing = isSuffixRequired && !houseNumberSuffixValue;

    if (isHouseSuffixMissing) return false;

    return postalCode === parameters?.postalCode && houseNumber === parameters?.houseNumber;
  }, [data, error, isValidInput, postalCode, houseNumber, isSuffixRequired, houseNumberSuffixValue, parameters]);

  useEffect(() => {
    setIsSuffixRequired(
      !!houseNumberSuffixOptions.length && !houseNumberSuffixOptions.some(suffix => suffix.value === emptySuffixString),
    );
  }, [houseNumberSuffixOptions, emptySuffixString]);

  // If houseNumberSuffixOptions exists set value of input to empty string to trigger validation
  // (since input is nullable, null avoids validation).
  useEffect(() => {
    if (isValidating || !parameters) return;

    resetField('houseNumberSuffix', {
      defaultValue: houseNumberSuffixOptions.length ? houseNumberSuffix : '',
    });
  }, [houseNumberSuffixOptions, isValidating, parameters, houseNumberSuffix, resetField]);

  const submitForm: SubmitHandler<FormValues> = ({ postalCode, houseNumber, houseNumberSuffix }) => {
    if (!isCurrentAddressValidated) return;
    send({
      type: Action.CAN_REGISTER_CONTINUE_ADDRESS,
      postalCode,
      houseNumber: Number(houseNumber),
      houseNumberSuffix,
    });
  };

  const getFullAddressStatus = () => {
    const getCopy = () => {
      if (data && isValidInput) return address.medium({ ...data, houseNumberSuffix });
      if (isValidating) return content.isLoadingAddressText;
      if (error) return content.addressUnknownText;

      return null;
    };

    const copy = getCopy();

    // return copy;
    if (copy) {
      return (
        <Text as="p" size="BodyM" color="textPrimary">
          {copy}
        </Text>
      );
    }
    return null;
  };

  const formErrorMessages: { [key: string]: string } = {
    postalCodeRequired: content.postalCodeFormField?.requiredMessage ?? '',
    postalCodeInvalid: content.postalCodeFormField?.validationMessage ?? '',
    houseNumberRequired: content.houseNumberFormField?.requiredMessage ?? '',
    houseNumberSuffixInvalid: content.houseNumberSuffixFormField?.validationMessage ?? '',
  };

  const handleOnFocus = () => {
    if (formHasReceivedFocus) return;
    trackingStart();
    setFormHasReceivedFocus(true);
  };

  return (
    <Stack gap="4">
      <Form onSubmit={handleSubmit(submitForm)} onFocus={handleOnFocus}>
        <Stack gap="6">
          <NavLink
            href={content.backLink?.href}
            variant="secondary"
            leftIcon={<ChevronLeftIcon />}>
            {content.backLink?.text}
          </NavLink>
          <Heading as="h1" size="XS">
            {content.CanRegisterAddress_title}
          </Heading>
          <Stack gap="3">
            <Text>{content.CanRegisterAddress_introductionText}</Text>

            {content.CanRegisterAddress_modalDialog?.triggerText && (
              <TrackedDialog
                trigger={
                  <TextLink rightIcon={<InfoIcon size="small" />}>
                    {content.CanRegisterAddress_modalDialog?.triggerText}
                  </TextLink>
                }
                title={content.CanRegisterAddress_modalDialog?.title}>
                <TrackViewComponent type="dialog" label={content.CanRegisterAddress_modalDialog?.title ?? ''}>
                  <Stack alignX="start" gap="4">
                    <RichText html={content.CanRegisterAddress_modalDialog?.content} />
                  </Stack>
                </TrackViewComponent>
              </TrackedDialog>
            )}
          </Stack>
          <Stack gap="2">
            <Stack direction="row" gap="2">
              <Stack.Item grow>
                <Controller
                  control={control}
                  name="postalCode"
                  render={({ field: { onChange, value, name } }) => (
                    <PostalCodeInput
                      error={errors?.postalCode?.message && formErrorMessages[errors.postalCode.message]}
                      label={content.postalCodeFormField?.label ?? ''}
                      name={name}
                      onChange={onChange}
                      placeholder={content.postalCodeFormField?.placeholder}
                      value={value}
                    />
                  )}
                />
              </Stack.Item>
              <Stack.Item grow>
                <Controller
                  control={control}
                  name="houseNumber"
                  render={({ field: { onChange, name, value } }) => (
                    <InputText
                      error={errors?.houseNumber?.message && formErrorMessages[errors.houseNumber.message]}
                      label={content.houseNumberFormField?.label ?? ''}
                      name={name}
                      placeholder={content.houseNumberFormField?.placeholder}
                      onChange={event => onChange(maskHouseNumber(event))}
                      value={value}
                    />
                  )}
                />
              </Stack.Item>
              <Stack.Item grow>
                <InputSelect
                  {...register('houseNumberSuffix')}
                  error={errors?.houseNumberSuffix?.message && formErrorMessages[errors.houseNumberSuffix.message]}
                  isDisabled={!houseNumberSuffixOptions.length}
                  options={houseNumberSuffixOptions}
                  placeholder={' '}
                  label={content.houseNumberSuffixFormField?.label ?? ''}
                />
              </Stack.Item>
            </Stack>
            {getFullAddressStatus()}
          </Stack>
          <Button type="submit" isLoading={isValidating}>
            {content.CanRegisterAddress_continueButtonText}
          </Button>
        </Stack>
      </Form>
      <Placeholder name="jss-registration-bottom" />
    </Stack>
  );
};
